import { content } from "./doc-content";
import { navigation } from "./navigation";

export interface SearchEntry {
  slug: string;
  title: string;
  description: string;
  sectionLabel: string;
  body: string;
}

// The search body text used to be a hand-maintained plain-text copy of every doc page's HTML.
// That duplication was the reason the two drifted apart — an edit to doc-content.ts had no
// effect on search results unless someone remembered to update this file too. Deriving the
// body from doc-content.ts's raw `content` record at module load keeps doc copy and search
// copy backed by a single source of truth. We use the raw (unresolved) HTML rather than the
// image-resolved variant so no asset URLs leak into the search text.
const HTML_ENTITY_REPLACEMENTS: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
};

const decodeHtmlEntities = (text: string): string =>
  text.replace(
    /&amp;|&lt;|&gt;|&quot;|&#39;|&nbsp;/g,
    (entity) => HTML_ENTITY_REPLACEMENTS[entity] ?? entity,
  );

const stripHtml = (html: string): string =>
  decodeHtmlEntities(
    html
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );

const buildBodies = (): Record<string, string> => {
  const bodies: Record<string, string> = {};
  for (const [slug, html] of Object.entries(content)) {
    bodies[slug] = stripHtml(html);
  }
  return bodies;
};

const bodies: Record<string, string> = buildBodies();

const buildIndex = (): SearchEntry[] => {
  const entries: SearchEntry[] = [];
  for (const section of navigation) {
    for (const page of section.pages) {
      entries.push({
        slug: page.slug,
        title: page.title,
        description: page.description,
        sectionLabel: section.label,
        body: bodies[page.slug] ?? "",
      });
    }
  }
  return entries;
};

export const searchIndex = buildIndex();

export interface SearchResult {
  entry: SearchEntry;
  matchType: "title" | "description" | "body";
  snippet: string;
}

// Locates the token at a word boundary rather than as a substring, so a token like "ai" finds
// its own word instead of the "ai" inside "email".
const extractSnippet = (text: string, token: string): string => {
  const match = buildWordBoundaryPrefixRegex(token, "i").exec(text);
  if (!match) return "";
  const idx = match.index;
  const start = Math.max(0, idx - 60);
  const end = Math.min(text.length, idx + match[0].length + 100);
  let snippet = text.slice(start, end).trim();
  if (start > 0) snippet = "…" + snippet;
  if (end < text.length) snippet = snippet + "…";
  return snippet;
};

// Short function words dominate prefix matching against unrelated pages: measured with "i"
// prefix-matching "Interview", which pushed plans-and-billing to rank 10 for a billing query.
const STOP_WORDS = new Set([
  "how",
  "can",
  "i",
  "a",
  "an",
  "the",
  "do",
  "does",
  "to",
  "my",
  "in",
  "on",
  "of",
  "for",
  "is",
  "it",
  "are",
  "with",
  "what",
  "where",
  "when",
  "why",
  "me",
  "you",
  "we",
]);

export const tokenizeQuery = (query: string): string[] => {
  const deduped = Array.from(
    new Set(
      query
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean),
    ),
  );

  // Avoid extremely noisy single-letter tokens (often produced by contractions/possessives like "what's" -> "s").
  const longEnough = deduped.filter((token) => token.length > 1);
  const withoutStopWords = longEnough.filter((token) => !STOP_WORDS.has(token));

  // Keeps stopword-only queries (e.g. "how to") answerable instead of returning zero results.
  return withoutStopWords.length > 0 ? withoutStopWords : longEnough;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// \b prevents "ai" from matching inside "email"; a prefix (no trailing \b) lets "invite" find "invites".
export const buildWordBoundaryPrefixRegex = (
  token: string,
  flags = "",
): RegExp => new RegExp(`\\b${escapeRegExp(token)}`, flags);

const FIELD_WEIGHTS = { title: 3, description: 2, body: 1 } as const;

export interface ScoredEntry {
  entry: SearchEntry;
  matchedTokens: string[];
  score: number;
}

// Distinct-matched-token count first, then weighted field score; Array#sort is stable so ties
// keep the input entries' order as the final tiebreaker.
const rankScored = (scored: ScoredEntry[]): ScoredEntry[] =>
  [...scored].sort((a, b) => {
    if (b.matchedTokens.length !== a.matchedTokens.length) {
      return b.matchedTokens.length - a.matchedTokens.length;
    }
    return b.score - a.score;
  });

// Shared by searchDocs and buildPassages (stores/help_center_chat.ts) so the modal's search
// results and the Ask sidebar's retrieved pages are ranked by the same rule. Returns entries
// already ranked. `entries` defaults to searchIndex; tests pass a fixture to exercise the
// ranking rule in isolation.
export const scoreEntries = (
  query: string,
  entries: SearchEntry[] = searchIndex,
): ScoredEntry[] => {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return [];

  const scored: ScoredEntry[] = [];
  for (const entry of entries) {
    const titleLower = entry.title.toLowerCase();
    const descLower = entry.description.toLowerCase();
    const bodyLower = entry.body.toLowerCase();

    const matchedTokens: string[] = [];
    let score = 0;
    for (const token of tokens) {
      const regex = buildWordBoundaryPrefixRegex(token);
      const inTitle = regex.test(titleLower);
      const inDesc = regex.test(descLower);
      const inBody = regex.test(bodyLower);
      if (!inTitle && !inDesc && !inBody) continue;
      matchedTokens.push(token);
      score +=
        (inTitle ? FIELD_WEIGHTS.title : 0) +
        (inDesc ? FIELD_WEIGHTS.description : 0) +
        (inBody ? FIELD_WEIGHTS.body : 0);
    }
    if (matchedTokens.length > 0) scored.push({ entry, matchedTokens, score });
  }
  return rankScored(scored);
};

export const searchDocs = (query: string, limit = 8): SearchResult[] => {
  const ranked = scoreEntries(query).slice(0, limit);

  return ranked.map(({ entry, matchedTokens }) => {
    const titleLower = entry.title.toLowerCase();
    const descLower = entry.description.toLowerCase();
    const matchesField = (fieldLower: string) =>
      matchedTokens.some((token) =>
        buildWordBoundaryPrefixRegex(token).test(fieldLower),
      );

    const matchType: SearchResult["matchType"] = matchesField(titleLower)
      ? "title"
      : matchesField(descLower)
        ? "description"
        : "body";

    let snippet = "";
    for (const token of matchedTokens) {
      snippet = extractSnippet(entry.body, token);
      if (snippet) break;
    }

    return { entry, matchType, snippet: snippet || entry.description };
  });
};
